import React, { useEffect, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { getAccount } from "../../../graphql/account/account-queries";

import {
  Grid,
  InputAdornment,
  makeStyles,
  TextField as TextFieldMui,
} from "@material-ui/core";

import {
  Create,
  FileInput,
  FormDataConsumer,
  FormTab,
  Loading,
  required,
  SelectInput,
  TabbedForm,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { useUser } from "../../../authenticatorApp/UserContext";
import {
  ACCEPTED_FILES_ADVICE,
  AppType,
  CarrierTypes,
  MAX_FILES,
  RA_REQUIRED,
  Workflow,
} from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { handleFileChange } from "../../../utils/fileUtils";
import { resolveScheduledDateTime } from "../../../utils/dateUtils";
import { resolveDefaultEventType } from "../../../managerApp/resources/eventTypes/EventTypeUtils";
import {
  AttachFileIcon,
  CreatedAtIcon,
} from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { FileField } from "../../../components/common/FileField";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { YardDateTimeInput } from "../../../components/common/YardDateTimeInput";
import { Scheduler } from "../../../components/common/scheduler/Scheduler";
import { ValidateCreateAdviceButton } from "./ValidateCreateAdviceButton";

const useStyles = makeStyles((theme) => ({
  fileInput: {
    marginTop: -theme.spacing(0),
  },
}));

const validateRequired = required();
const validateRequiredFiles = (value) =>
  Array.isArray(value) && value.length > 0 ? undefined : RA_REQUIRED;

const fetchData = async (
  appUser,
  setAccount,
  setEventType,
  setCanSchedule,
  setLoading,
  notify,
) => {
  try {
    setLoading(true);
    const resp = await API.graphql(
      graphqlOperation(getAccount, { id: appUser.accountId }),
    );
    const accountData = resp.data.getAccount;

    // Render account data first so a scheduling-resolution failure below only
    // disables the Scheduler instead of blanking the whole form.
    setAccount(accountData);

    const { eventType, canSchedule } = await resolveDefaultEventType(
      accountData?.inboundEventTypeId,
      Workflow.INBOUND,
    );
    setEventType(eventType);
    setCanSchedule(canSchedule);
  } catch (err) {
    logError("inbounds:InboundAdviceCreate:fetchData", err, notify);
  } finally {
    setLoading(false);
  }
};

// AI-first inbound advice create. Unlike the manager AdviceCreateForm there is
// no account picker — the account is the logged-in customer's. The customer
// picks Load Type + Carrier Type and drops the advice document; the
// /parseInboundAdvice pipeline extracts the remaining fields.
const ShipmentSetupTab = ({
  account,
  eventType,
  canSchedule,
  selectedTimeslot,
  setSelectedTimeslot,
}) => {
  const classes = useStyles();
  const { appUser } = useUser();
  const form = useForm();
  const notify = useNotify();
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const yardCommodities =
    window.appUser?.reloadYard?.activeYardCommodities?.items ?? [];

  const handleSchedulerOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canSchedule) setIsSchedulerOpen(true);
  };

  const ignoreEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTimeslot = (timeslot) => {
    const scheduledDate = resolveScheduledDateTime(timeslot, {
      notify,
      scope: "inbounds:InboundAdviceCreate:handleTimeslot",
    });
    if (!scheduledDate) return;
    form.change("scheduledDateTime", scheduledDate);
    setSelectedTimeslot(timeslot);
  };

  const getSchedulerHelperText = () =>
    canSchedule
      ? false
      : `Contact ${appUser?.reloadYard?.name} to setup an event type.`;

  return (
    <FormDataConsumer>
      {() => (
        <Grid container direction="row" spacing={3}>
          <Grid item container direction="column" spacing={0} lg={8}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={4}>
                <TextFieldMui
                  label="Transload Yard"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  value={appUser?.reloadYard?.name || ""}
                  disabled
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectInput
                  source="reloadYardCommodityId"
                  label="Load Type"
                  variant="outlined"
                  fullWidth
                  choices={yardCommodities}
                  optionText={(choice) =>
                    `${choice.commodity.name} - ${choice.units}`
                  }
                  optionValue="id"
                  validate={validateRequired}
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectInput
                  source="carrierType"
                  label="Carrier Type"
                  variant="outlined"
                  fullWidth
                  choices={CarrierTypes}
                  validate={validateRequired}
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <YardDateTimeInput
                  source="scheduledDateTime"
                  label="Schedule Delivery"
                  variant="outlined"
                  fullWidth
                  disabled={!canSchedule}
                  onClick={handleSchedulerOpen}
                  onKeyPress={ignoreEvent}
                  options={{
                    readOnly: true,
                    InputProps: {
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CreatedAtIcon onClick={handleSchedulerOpen} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  helperText={getSchedulerHelperText()}
                />
                {canSchedule && (
                  <Scheduler
                    eventType={eventType}
                    workflow={Workflow.INBOUND}
                    account={account}
                    appType={AppType.CUSTOMER}
                    open={isSchedulerOpen}
                    setIsOpen={setIsSchedulerOpen}
                    resettable={false}
                    selectedTimeslot={selectedTimeslot}
                    setSelectedTimeslot={handleTimeslot}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item container direction="row" spacing={0} xs={false} md={4}>
            <Grid item xs={12} id="attachments-advice-input">
              <FileInput
                source="attachments"
                label="Inbound Advice Attachments"
                variant="outlined"
                fullWidth
                className={classes.fileInput}
                validate={validateRequiredFiles}
                helperText="Accepted files: PNG, JPEG, PDF, or TIFF."
                placeholder={
                  <p>
                    <AttachFileIcon fontSize="large" /> <br /> Drop files to
                    upload, or click to select.
                  </p>
                }
                accept={ACCEPTED_FILES_ADVICE}
                multiple
                maxFiles={MAX_FILES}
                options={{ maxFiles: MAX_FILES }}
                onChange={(value) =>
                  handleFileChange("attachments", value, form, notify)
                }
              >
                <FileField source="src" title="title" target="_blank" />
              </FileInput>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CreateActions = ({ basePath }) => (
  <TopToolbar>
    <BackButton basePath={basePath} />
  </TopToolbar>
);

const CreateToolbar = ({ appUser, account, selectedTimeslot, ...props }) => (
  <Toolbar {...props}>
    <ValidateCreateAdviceButton
      appUser={appUser}
      account={account}
      selectedTimeslot={selectedTimeslot}
    />
  </Toolbar>
);

export const InboundAdviceCreate = (props) => {
  const notify = useNotify();
  const { appUser } = useUser();
  const [account, setAccount] = useState();
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState();
  const [canSchedule, setCanSchedule] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState();

  useEffect(() => {
    fetchData(
      appUser,
      setAccount,
      setEventType,
      setCanSchedule,
      setLoading,
      notify,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser]);

  if (loading || !account) return <Loading />;
  return (
    <Create
      {...props}
      title={<ForceTitle title="New Inbound Advice" />}
      actions={<CreateActions {...props} />}
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <CreateToolbar
            appUser={appUser}
            account={account}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormTab label="Inbound Advice">
          <ShipmentSetupTab
            account={account}
            eventType={eventType}
            canSchedule={canSchedule}
            selectedTimeslot={selectedTimeslot}
            setSelectedTimeslot={setSelectedTimeslot}
          />
        </FormTab>
      </TabbedForm>
    </Create>
  );
};
